<template>
  <v-container>
    <ContentHeader
      header-icon="mdi-rocket-launch-outline"
      header-title="Feature Flags"
      back-navigation
      back-navigation-href="/admin"
      back-navigation-text="To Old Admin"
    >
    </ContentHeader>
    <PtSheet>
      <v-row>
        <v-col>
          <v-data-table
            :headers="headers"
            :items="visibleFeatures"
            item-key="id"
            show-expand
            single-expand
            hide-default-footer
            disable-sort
            class="body-desktop-medium-regular-16"
            :expanded.sync="expanded"
            item-class="rowClass"
          >
            <template v-slot:item.name="props">
              <div
                v-if="!props.item.productionReady"
                class="in-development-watermark"
              >
                <v-icon color="colorSemanticStateYellow300"
                  >mdi-traffic-cone</v-icon
                >
              </div>
              <div class="fill-height d-flex flex-column justify-center">
                <div class="flag-title">
                  {{ props.item.name }}
                </div>
                <div v-if="expanded[0] !== props.item" class="flag-description">
                  {{ props.item.description }}
                </div>
              </div>
            </template>
            <template v-slot:item.type="props">
              {{ featureType(props.item) }}
            </template>
            <template v-slot:item.createdDate="props">
              {{ formatDate(props.item.createdDate) }}
            </template>
            <template v-slot:item.flag="{ item }">
              <div v-if="item.enabled" class="flag-on text-right mr-n3">
                ON
              </div>
              <div v-else class="flag-off text-right mr-n3">
                OFF
              </div>
            </template>
            <template v-slot:item.switch="{ item }">
              <div class="d-flex">
                <div>
                  <v-switch
                    inset
                    color="colorSemanticStateGreen600"
                    v-model="item.enabled"
                    :disabled="
                      (item.irreversible &&
                        item.enabled !== item.initialState) ||
                        (!item.productionReady && inProduction)
                    "
                    @change="tryToggleFeature(item)"
                  >
                  </v-switch>
                </div>
                <div class="d-flex align-center">
                  <v-tooltip
                    top
                    v-if="
                      item.irreversible && item.enabled !== item.initialState
                    "
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        color="colorSemanticStateGreen200"
                        v-bind="attrs"
                        v-on="on"
                        small
                        class="switch-lock"
                      >
                        mdi-lock
                      </v-icon>
                    </template>
                    <span>
                      This feature has been activated and can no longer be
                      reversed
                    </span>
                  </v-tooltip>
                </div>
              </div>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <div class="pl-12 py-4">
                  <p>{{ item.description }}</p>
                  <h2 class="body-desktop-medium-bold-16 mb-4">
                    Documentation
                  </h2>
                  <p
                    v-for="(document, docIndex) in item.documents"
                    :key="docIndex"
                  >
                    <v-icon>mdi-file-outline</v-icon>
                    {{ document.name }}:
                    <a :href="document.link" target="_blank">
                      {{ document.link }}
                    </a>
                  </p>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </PtSheet>

    <v-dialog v-model="confirmationDialog" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="colorAlertRed">mdi-alert-rhombus</v-icon>
          <v-btn icon absolute top right @click="cancelToggle()">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          WARNING: Enabling this feature is an irreversible operation. Are you
          sure you wish to continue?
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelToggle()">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="confirmToggle()">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import hasNotifications from "@/mixins/has-notifications";
import { get, put } from "@/services/request";
import { featureFlagsAPI, featureFlagByIdAPI } from "@/services/api";
import ContentHeader from "@/views/ContentHeader.vue";
import PtSheet from "@/components/PtSheet.vue";
import store from "@/store";
export default {
  name: "FeatureFlags",
  components: { PtSheet, ContentHeader },
  mixins: [hasNotifications],
  data() {
    return {
      expanded: [],
      features: [],
      headers: [
        { text: "Name", value: "name" },
        { text: "Type", value: "type" },
        { text: "Created Date", value: "createdDate" },
        { value: "flag" },
        { value: "switch" }
      ],
      confirmationDialog: false,
      featureFlag: null
    };
  },
  created() {
    this.fetchFeatures();
  },
  computed: {
    inProduction() {
      return this.$store.getters.inProduction;
    },
    visibleFeatures() {
      return this.features.filter(feature => !feature.hidden);
    }
  },
  methods: {
    featureType(feature) {
      return feature.irreversible ? "Irreversible" : "Standard";
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    fetchFeatures() {
      get(featureFlagsAPI)
        .then(response => {
          if (response && response.data) {
            this.features = response.data;
          }
        })
        .catch(() => {
          this.showMessage({
            text: "Couldn't load feature toggles",
            mode: "error"
          });
        });
    },
    tryToggleFeature(feature) {
      if (feature.irreversible) {
        this.featureFlag = feature;
        this.confirmationDialog = true;
      } else {
        this.toggleFeature(feature);
      }
    },
    confirmToggle() {
      this.confirmationDialog = false;
      this.toggleFeature(this.featureFlag);
    },
    cancelToggle() {
      this.confirmationDialog = false;
      this.featureFlag.enabled = !this.featureFlag.enabled;
    },
    toggleFeature(feature) {
      put(featureFlagByIdAPI.getUrl({ id: feature.id }), {
        enabled: feature.enabled
      })
        .then(response => {
          if (response) {
            store.dispatch("featureFlags/fetchFlags");
          }
        })
        .catch(error => {
          this.showMessage({
            text: error,
            mode: "error"
          });
        })
        .finally(() => {
          // Fetch the updated features after toggling
          this.fetchFeatures();
        });
    }
  }
};
</script>

<style scoped lang="scss">
.flag-title {
  font-size: 20px;
  line-height: 25px;
}

.flag-description {
  width: 50px;
  min-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  color: $color-neutrals-navy-200;
}

::v-deep .v-data-table__expanded.v-data-table__expanded__row > td {
  border-bottom: 0 !important;
}

::v-deep .v-data-table__expanded.v-data-table__expanded__content {
  border-top: 0;
  box-shadow: none;
}

.flag-on {
  font-size: 18px;
  font-weight: 500;
  color: $color-semantic-state-green-600;
}

.flag-off {
  font-size: 18px;
  font-weight: 500;
  color: $color-neutrals-navy-200;
}

.in-development-watermark {
  position: absolute;
  text-align: center;
  z-index: 0;
  opacity: 0.3;
  margin-top: 4px;
  margin-left: -34px;

  .v-icon {
    font-size: 56px;
  }
}

.switch-lock {
  margin-left: -33.5px; //it just looked off, don't judge me
  margin-bottom: 3px;
}
</style>
